import { useState } from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useLang } from '../i18n/LanguageContext';
import VideoModal from './VideoModal';
import styles from './Ai.module.css';

function PlayIcon() {
  return (
    <svg width="44" height="44" viewBox="0 0 40 40" fill="none" aria-hidden="true">
      <circle cx="20" cy="20" r="19" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" />
      <path d="M17 14l12 6-12 6V14z" fill="rgba(255,255,255,0.95)" />
    </svg>
  );
}

export default function Ai() {
  const revealRef = useScrollReveal();
  const { t, tr } = useLang();
  const [active, setActive] = useState(null);

  const openExample = () =>
    setActive({
      videoId: 'ai-radibor',
      youtubeId: 'Hw0WfXQSGiQ',
      title: 'School in Radibor — Cornelsen Zukunftspreis 2026',
    });

  return (
    <section id="ai" className={styles.section}>
      <div className={`container reveal ${styles.inner}`} ref={revealRef}>
        <p className="section-label">{t('ai.eyebrow')}</p>
        <h2 className={styles.headline}>
          {tr('Not an AI studio. Not against AI either.', 'Kein KI-Studio. Aber auch nicht gegen KI.')}
        </h2>
        <p className={styles.subtext}>
          {tr(
            <>
              Both of those are fashionable positions right now. We&apos;re somewhere in the
              middle, and we&apos;d rather just tell you where.
            </>,
            <>
              Beides sind gerade modische Positionen. Wir liegen irgendwo dazwischen und
              erzählen Ihnen lieber einfach, wo.
            </>,
          )}
        </p>

        <div className={styles.layout}>
          <div className={styles.copy}>
            <p>
              {tr(
                <>
                  This site was built with AI&apos;s help, and so were a fair few of the words on it
                  — including some of these. In the edit, AI cleans up audio we couldn&apos;t fix any
                  other way, and it takes care of the first round of subtitles too — although we
                  always find ourselves adjusting them as well. Now and then it builds a title or an
                  animation we couldn&apos;t shoot in-camera. None of that is a secret. None of it
                  replaces showing up and filming the thing.
                </>,
                <>
                  Diese Seite wurde mit KI-Hilfe gebaut, und das gilt auch für so manche Worte
                  hier — einschließlich einiger davon. Im Schnitt räumt KI Audio auf, das wir auf
                  keine andere Weise in den Griff bekamen, und sie übernimmt auch die erste Runde
                  Untertitel — obwohl wir immer wieder selbst daran justieren. Hin und wieder baut
                  sie einen Titel oder eine Animation, die wir nicht in-camera drehen konnten.
                  Nichts davon ist ein Geheimnis. Nichts davon ersetzt es, hinzufahren und die Sache
                  zu filmen.
                </>,
              )}
            </p>
            <p>
              {tr(
                <>
                  We&apos;re not an AI studio. We&apos;re a small crew picking up a new tool and
                  getting better at it — and we like it most when it earns its keep, somewhere a camera
                  alone wouldn&apos;t have been enough.
                </>,
                <>
                  Wir sind kein KI-Studio. Wir sind ein kleines Team, das sich ein neues Werkzeug
                  aneignet und damit besser wird — und wir mögen es am meisten, wenn es sich
                  auszahlt, an einer Stelle, an der eine Kamera allein nicht gereicht hätte.
                </>,
              )}
            </p>
          </div>

          <div className={styles.example}>
            <button
              type="button"
              className={styles.exampleThumb}
              onClick={openExample}
              aria-label={tr(
                'Watch the Radibor example: Cornelsen Zukunftspreis 2026',
                'Radibor-Beispiel ansehen: Cornelsen Zukunftspreis 2026',
              )}
            >
              <img
                src="/images/work/cornelsen-zukunftspreis-radibor.webp"
                alt={tr(
                  "A castle at dusk — the Radibor film's closing surprise frame",
                  'Ein Schloss in der Dämmerung — die überraschende Schluss-Einstellung des Radibor-Films',
                )}
                className={styles.examplePoster}
                loading="lazy"
                decoding="async"
              />
              <span className={styles.exampleOverlay} aria-hidden="true" />
              <span className={styles.examplePlay}>
                <PlayIcon />
                <span className={styles.exampleLabel}>
                  {tr('Watch the example', 'Beispiel ansehen')}
                </span>
              </span>
            </button>

            <div className={styles.exampleBody}>
              <p className={styles.exampleTag}>
                {tr('Example · Cornelsen Zukunftspreis', 'Beispiel · Cornelsen Zukunftspreis')}
              </p>
              <h3 className={styles.exampleTitle}>
                {tr(
                  '\u201CMit Harry Potter durch das Schuljahr\u201D — School in Radibor',
                  '\u201CMit Harry Potter durch das Schuljahr\u201D — Schule in Radibor',
                )}
              </h3>
              <p className={styles.exampleText}>
                {tr(
                  <>
                    The Radibor school teaches kids to read with Harry Potter. At the end of their
                    film, there&apos;s a surprise — a small, AI-generated moment we simply couldn&apos;t
                    have shot. It lasts a few seconds, and it&apos;s the kind of touch that makes a
                    film feel made instead of delivered.
                  </>,
                  <>
                    Die Radibor-Schule bringt Kindern mit Harry Potter das Lesen bei. Am Ende ihres
                    Films gibt es eine Überraschung — ein kleiner, KI-generierter Moment, den wir
                    einfach nicht hätten drehen können. Er dauert ein paar Sekunden, und er ist die
                    Art von Detail, die einen Film gemacht statt nur geliefert wirken lässt.
                  </>,
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      <VideoModal
        isOpen={!!active}
        onClose={() => setActive(null)}
        title={active?.title ?? ''}
        videoId={active?.videoId ?? ''}
        youtubeId={active?.youtubeId}
      />
    </section>
  );
}
